import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

/** Presets for AI preferences */
export const aiPreferencesPresetEnums = ['exact', 'efficient', 'concise'] as const;
export const AiPreferencesPresetEnumsSchema = z.enum(aiPreferencesPresetEnums);
export type AiPreferencesPresetEnums = z.infer<typeof AiPreferencesPresetEnumsSchema>;

/**
 * Supported languages for AI-generated notes
 * This is the single source of truth for language configuration across the application
 */
export const aiNoteLanguageEnums = [
  'Arabic',
  'Danish',
  'Dutch',
  'English',
  'French',
  'German',
  'Italian',
  'Japanese',
  'Norwegian',
  'Portuguese',
  'Spanish',
  'Swedish',
] as const;
export const AiNoteLanguageSchema = z.enum(aiNoteLanguageEnums).openapi({
  description: 'Language for AI-generated notes',
  example: 'English',
});
export type AiNoteLanguage = z.infer<typeof AiNoteLanguageSchema>;

/**
 * Default language for AI-generated notes
 */
export const AI_NOTE_LANGUAGE_DEFAULT: AiNoteLanguage = 'English';

/**
 * Language options for dropdown menus
 * Provides label/value pairs for UI components
 */
export const aiNoteLanguageOptions = aiNoteLanguageEnums.map(lang => ({
  label: lang,
  value: lang,
})) as { label: AiNoteLanguage; value: AiNoteLanguage }[];

/**
 * Timesheet Assistant preferences schema (extracted for reuse across workspace/global contexts)
 */
export const TimesheetAssistantSchema = z.object({
  interval_minutes: z.number().int().min(5).max(1440).default(15).openapi({
    description: 'Interval in minutes for timesheet generation chunking (5 minutes to 24 hours)',
    example: 15,
  }),
  provider: z.enum(['Anthropic', 'OpenAI']).default('OpenAI').openapi({
    description: 'AI provider to use for timesheet generation',
    example: 'OpenAI',
  }),
  effort: z.enum(['none', 'low', 'medium']).default('low').openapi({
    description: 'Effort level for timesheet generation processing',
    example: 'low',
  }),
  note_language: AiNoteLanguageSchema.default(AI_NOTE_LANGUAGE_DEFAULT).openapi({
    description: 'Language for generated timesheet notes',
    example: 'English',
  }),
  daily_generation: z.enum(['weekdays_only', 'every_day', 'off']).default('weekdays_only').openapi({
    description: 'Frequency of daily email summaries',
    example: 'off',
  }),
  last_reset_at: z.string().datetime({ offset: true }).nullable().optional().openapi({
    description:
      'Timestamp of when AI memory was last reset. AI will ignore data before this time.',
    example: '2024-03-20T22:59:59.000Z',
  }),
  interval_model: z.string().nullable().optional().openapi({
    description:
      'Internal (Timely staff) only — OpenAI model id for phase-1 project assignment. Applied by the AI engine only for staff users. Null/absent = stock behavior.',
    example: 'gpt-5',
  }),
  summary_model: z.string().nullable().optional().openapi({
    description:
      'Internal (Timely staff) only — OpenAI model id for phase-2 note prose. Applied by the AI engine only for staff users. Null/absent = stock behavior.',
    example: 'gpt-5.1',
  }),
  interval_custom_instructions: z.string().max(20000).nullable().optional().openapi({
    description:
      'Internal (Timely staff) only — free-text custom instructions the AI engine APPENDS (with precedence) to its phase-1 project-assignment prompt. Applied only for staff users. Null/absent = stock behavior.',
  }),
  summary_custom_instructions: z.string().max(20000).nullable().optional().openapi({
    description:
      'Free-text custom instructions the AI engine APPENDS (with precedence) to its phase-2 note-prose prompt. Set per user by Timely staff, or as a workspace default by workspace admins. Null/absent = stock behavior.',
  }),
  interval_rule_overrides: z
    .record(z.string(), z.string().max(4000))
    .nullable()
    .optional()
    .openapi({
      description:
        'Internal (Timely staff) only — per-rule replacements for named baseline rules of the phase-1 prompt, keyed by engine rule id (from the staff model-options endpoint). The engine appends each as a targeted override naming the stock rule it cancels. Null/absent = stock behavior.',
    }),
  summary_rule_overrides: z.record(z.string(), z.string().max(4000)).nullable().optional().openapi({
    description:
      'Per-rule replacements for named baseline rules of the phase-2 note prompt, keyed by engine rule id. Set per user by Timely staff, or as a workspace default by workspace admins. Null/absent = stock behavior.',
  }),
});
export type TimesheetAssistant = z.infer<typeof TimesheetAssistantSchema>;

/** Rule-map patch: a `null` value deletes that rule's override; `null` for the whole map clears it. */
const RuleOverridesPatchSchema = z
  .record(z.string(), z.string().max(4000).nullable())
  .nullable()
  .optional();

/**
 * Patch shape for writing workspace defaults. The server merges it over the
 * saved `timesheet_assistant` document key by key; the two rule maps merge
 * per rule id, where a `null` value deletes that rule's override.
 */
export const TimesheetAssistantPatchSchema = TimesheetAssistantSchema.partial().extend({
  interval_rule_overrides: RuleOverridesPatchSchema.openapi({
    description:
      'Per-rule patch for the phase-1 rule overrides: keys merge into the saved map, a null value deletes that key, null for the whole map clears it.',
  }),
  summary_rule_overrides: RuleOverridesPatchSchema.openapi({
    description:
      'Per-rule patch for the phase-2 rule overrides: keys merge into the saved map, a null value deletes that key, null for the whole map clears it.',
  }),
});
export type TimesheetAssistantPatch = z.infer<typeof TimesheetAssistantPatchSchema>;

/** Keys of `timesheet_assistant` that carry admin- or staff-authored instruction text. */
export const timesheetAssistantInstructionTextFields = [
  'summary_custom_instructions',
  'summary_rule_overrides',
  'interval_custom_instructions',
  'interval_rule_overrides',
] as const;

/** Valid field names for locking in workspace defaults (provider and effort are internal-only) */
export const timesheetAssistantLockableFields = [
  'interval_minutes',
  'note_language',
  'daily_generation',
] as const;
export type TimesheetAssistantLockableField = (typeof timesheetAssistantLockableFields)[number];

/**
 * AI Preferences (global, per-user)
 */
export type AiPreferences = z.infer<typeof AiPreferencesSchema>;
export const AiPreferencesSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  enabled: z.boolean().default(false).openapi({
    description:
      'Is AI enabled for this user? Default true is used to draw initial UI, but AI will NOT activate until user confirms.',
    example: true,
  }),
  show_timesheet_predictions: z.boolean().default(false).openapi({
    description: 'Show predictions on the timesheet itself?',
  }),
  show_timeline_predictions: z.boolean().default(true).openapi({
    description: 'Show predictions on the timeline?',
  }),
  timeline_layout: z.enum(['compact', 'wide']).default('compact').openapi({
    description: 'How predictions are presented on the timeline',
    example: 'compact',
  }),
  updated_at: z.string().datetime({ offset: true }).optional().nullable().openapi({
    description:
      'When were these preferences last updated? If null/undefined, it means the user has not set any preferences yet.',
    example: '2024-03-20T22:59:59.000Z',
  }),
  preset: z
    .enum(['exact', 'efficient', 'concise'])
    .default('efficient')
    .openapi({
      description: 'Preset for the AI prediction settings',
      example: 'efficient',
    })
    .nullable(),
  fill_gap: z.number().int().min(0).max(60).default(5).openapi({
    description: 'How many minutes of smoothing / filling of gaps should be done?',
    example: 5,
  }),
  min_duration: z.number().int().min(0).max(15).default(5).openapi({
    description: 'Minimum duration of a prediction in minutes',
    example: 5,
  }),
  train_tags: z.boolean().default(true).openapi({
    description: 'Should the AI predict tags/labels? Results in higher volume of predictions.',
  }),
  suggest_note: z.boolean().default(false).openapi({
    description: 'Should the AI predict notes? ',
  }),
  relaxed_tag_matching: z.boolean().default(false).openapi({
    description:
      'Should the client-side be more relaxed when matching tags to existing time entries?',
  }),
  relaxed_note_matching: z.boolean().default(false).openapi({
    description:
      'Should the client-side be more relaxed when matching notes to existing time entries?',
  }),
  timesheet_assistant: TimesheetAssistantSchema.default({
    interval_minutes: 15,
    provider: 'OpenAI',
    effort: 'low',
    note_language: 'English',
    last_reset_at: null,
  }).openapi({
    description: 'Timesheet assistant specific preferences',
  }),
});

/**
 * Workspace-level timesheet assistant preferences (per-user, per-account)
 */
export type AiWorkspacePreferences = z.infer<typeof AiWorkspacePreferencesSchema>;
export const AiWorkspacePreferencesSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  timely_account_id: z.number().int(),
  timesheet_assistant: TimesheetAssistantSchema.default({
    interval_minutes: 15,
    provider: 'OpenAI',
    effort: 'low',
    note_language: 'English',
    last_reset_at: null,
  }),
  updated_at: z.string().datetime({ offset: true }).optional().nullable(),
});

/**
 * Resolved workspace preferences returned to the frontend.
 * Includes the resolved values and which fields are locked by admin.
 */
export type ResolvedWorkspacePreferences = z.infer<typeof ResolvedWorkspacePreferencesSchema>;
export const ResolvedWorkspacePreferencesSchema = z.object({
  timesheet_assistant: TimesheetAssistantSchema,
  // Deliberately looser than the write schema, which is a
  // `z.enum(timesheetAssistantLockableFields)`. A row written before that
  // enum existed may hold a field outside it, and failing the parse here
  // would drop the whole record and switch the workspace's enforcement
  // off. The resolver filters to the allowlist instead, so an unexpected
  // entry simply is not enforced. See queryResolveTimesheetAssistant.
  locked_fields: z
    .array(z.string())
    .default([])
    .openapi({
      description:
        'Fields that are locked/enforced by workspace admin and cannot be changed by users',
      example: ['interval_minutes', 'note_language'],
    }),
  has_note_instructions: z.boolean().default(false).openapi({
    description:
      'Whether a workspace admin has set summary note instructions. A boolean only, never the text — this response goes to every member, so members learn that instructions are active without requesting the admin document at all.',
    example: false,
  }),
});

/**
 * Workspace defaults set by admins (enforced/locked settings)
 */
export type AiWorkspaceDefaults = z.infer<typeof AiWorkspaceDefaultsSchema>;
export const AiWorkspaceDefaultsSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  timely_account_id: z.number().int(),
  timesheet_assistant: TimesheetAssistantSchema.partial().default({}),
  locked_fields: z
    .array(z.string())
    .default([])
    .openapi({
      description: 'Field names that are enforced for all users in this workspace',
      example: ['interval_minutes', 'note_language'],
    }),
  has_note_instructions: z.boolean().optional().openapi({
    description:
      'Whether a workspace admin has set summary note instructions (freeform text or at least one rule override). Members get this flag only; the instruction text fields are returned to admins and owners.',
    example: false,
  }),
  updated_at: z.string().datetime({ offset: true }).optional().nullable(),
});

/**
 * Body for saving workspace defaults. `timesheet_assistant` is a patch merged
 * server-side over the saved document; `locked_fields` replaces the saved list
 * when present and leaves it alone when omitted.
 */
export type AiWorkspaceDefaultsPatch = z.infer<typeof AiWorkspaceDefaultsPatchSchema>;
export const AiWorkspaceDefaultsPatchSchema = z.object({
  timesheet_assistant: TimesheetAssistantPatchSchema.default({}).openapi({
    description:
      'Patch merged over the saved timesheet_assistant document. Keys not present are kept; null clears a nullable key; rule maps merge per rule id.',
  }),
  locked_fields: z
    .array(z.enum(timesheetAssistantLockableFields))
    .optional()
    .openapi({
      description:
        'Replaces the enforced-field list when present. Omit to keep the saved list unchanged. Only interval_minutes, note_language and daily_generation may be locked.',
      example: ['interval_minutes', 'note_language'],
    }),
});
